import { LOCALE, t } from "../i18n";
/** Formatação e conversões de unidades no idioma em uso (LOCALE). Ritmo sempre em min/km, distância em km. */

const dateFmt = new Intl.DateTimeFormat(LOCALE, { timeZone: "UTC" });
const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, { dateStyle: "short", timeStyle: "short" });
const kmFmt = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 });

/** Datas "YYYY-MM-DD" são datas civis: formatar em UTC evita deslocar o dia pelo fuso. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return dateFmt.format(new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso));
}

export function formatDateTime(iso: string | null | undefined): string {
  return iso ? dateTimeFmt.format(new Date(iso)) : "—";
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toLocaleString(LOCALE, { maximumFractionDigits: n < 10240 ? 1 : 0 })} KB`;
  return `${(n / 1024 / 1024).toLocaleString(LOCALE, { maximumFractionDigits: 1 })} MB`;
}

export function metersToKm(m: number | null | undefined): string {
  return m === null || m === undefined ? "—" : `${kmFmt.format(m / 1000)} km`;
}

/** 3725 -> "1:02:05"; 1500 -> "25:00" */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined) return "—";
  const s = Math.round(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(h ? 2 : 1, "0");
  return h ? `${h}:${mm}:${String(sec).padStart(2, "0")}` : `${mm}:${String(sec).padStart(2, "0")}`;
}

/** "1:02:05" | "25:00" | "45" (minutos) -> segundos. Retorna null se inválido. */
export function parseDuration(text: string): number | null {
  const tp = text.trim();
  if (!tp) return null;
  if (!/^\d{1,3}(:\d{1,2}){0,2}$/.test(tp)) return null;
  const parts = tp.split(":").map(Number);
  if (parts.slice(1).some((p) => p >= 60)) return null;
  if (parts.length === 1) return parts[0] * 60;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] * 3600 + parts[1] * 60 + parts[2];
}

/** segundos por km -> "5:30 /km" */
export function formatPace(secondsPerKm: number | null | undefined): string {
  if (!secondsPerKm) return "—";
  const m = Math.floor(secondsPerKm / 60);
  const s = Math.round(secondsPerKm % 60);
  return `${m}:${String(s).padStart(2, "0")} /km`;
}

/** "5:30" -> 330 */
export function parsePace(text: string): number | null {
  const m = /^(\d{1,2}):([0-5]\d)$/.exec(text.trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Ritmo médio de uma marca: tempo / distância. */
export function paceOf(timeS: number, distanceM: number): number {
  return timeS / (distanceM / 1000);
}

export const WEEKDAYS = [t("Seg"), t("Ter"), t("Qua"), t("Qui"), t("Sex"), t("Sáb"), t("Dom")];

export const EXPERIENCE_LABEL: Record<string, string> = {
  beginner: t("Iniciante"),
  intermediate: t("Intermediário"),
  advanced: t("Avançado"),
  elite: t("Elite"),
};

export const PREFERRED_TIME_LABEL: Record<string, string> = {
  morning: t("Manhã"),
  afternoon: t("Tarde"),
  evening: t("Noite"),
  flexible: t("Flexível"),
};

export const STATUS_LABEL: Record<string, string> = {
  active: t("Ativo"),
  paused: t("Pausado"),
  inactive: t("Inativo"),
};

export const ROLE_LABEL: Record<string, string> = {
  org_manager: t("Gestão"),
  coach: t("Treinador"),
  athlete: t("Atleta"),
};

export const DISTANCE_PRESETS = [
  { label: "5 km", m: 5000 },
  { label: "10 km", m: 10000 },
  { label: "21,1 km", m: 21097 },
  { label: "42,2 km", m: 42195 },
];

export function distanceLabel(m: number): string {
  return DISTANCE_PRESETS.find((p) => p.m === m)?.label ?? metersToKm(m);
}

/** Faixa "a–b" tolerante a valores ausentes: (130, null) -> "a partir de 130". */
export function rangeText(min: number | null, max: number | null, fmt: (v: number) => string = String): string {
  if (min == null && max == null) return "—";
  if (min != null && max != null) return `${fmt(min)}–${fmt(max)}`;
  return min != null ? t("a partir de {min}", { min: fmt(min) }) : t("até {max}", { max: fmt(max as number) });
}

export const paceShort = (s: number) => formatPace(s).replace(" /km", "");
